import {ThumbsdownIcon, ThumbsupIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {useRef, useState} from 'react'
import {NegativeFeedbackForm} from './NegativeFeedbackForm'
import type {FeedbackSubmitHandler, FeedbackOptions} from './types'

interface PositiveFeedbackButtonProps {
  disabled: boolean
  commentId: string
  onClick: FeedbackSubmitHandler
}

export const PositiveFeedbackButton: React.FC<PositiveFeedbackButtonProps> = ({onClick, commentId, disabled}) => (
  <IconButton
    size="small"
    variant="invisible"
    disabled={disabled}
    icon={ThumbsupIcon}
    title="Positive feedback"
    aria-label="Positive feedback"
    onClick={() => onClick({type: 'POSITIVE', commentId})}
  />
)

interface NegativeFeedbackButtonProps {
  disabled: boolean
  commentId: string
  onClick: FeedbackSubmitHandler
  feedbackOptions?: FeedbackOptions
  showNegativeFeedbackModal?: boolean
}

export const NegativeFeedbackButton: React.FC<NegativeFeedbackButtonProps> = ({
  onClick,
  commentId,
  disabled,
  feedbackOptions,
  showNegativeFeedbackModal = true,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const returnFocusRef = useRef<HTMLButtonElement>(null)

  const handleSubmit: FeedbackSubmitHandler = args => {
    onClick(args)
    setDialogOpen(false)
  }

  const handleClick = () => {
    if (showNegativeFeedbackModal) {
      setDialogOpen(true)
    } else {
      // Submit negative feedback directly without showing modal
      onClick({type: 'NEGATIVE', commentId})
    }
  }

  return (
    <>
      {dialogOpen && showNegativeFeedbackModal && (
        <NegativeFeedbackForm
          onClose={() => setDialogOpen(false)}
          onSubmit={handleSubmit}
          commentId={commentId}
          feedbackOptions={feedbackOptions}
          returnFocusRef={returnFocusRef}
        />
      )}
      <IconButton
        ref={returnFocusRef}
        size="small"
        variant="invisible"
        disabled={disabled}
        icon={ThumbsdownIcon}
        title="Negative feedback"
        aria-label="Negative feedback"
        onClick={handleClick}
      />
    </>
  )
}
